/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getDevice, getDTCCodes, getEvents} from '@ion/core/src/services/Connect';
import Device from '@ion/core/src/services/Device';
import {BaseAsset, CVSAssetType} from '@ion/core/src/types/entities';
import {
  Attribute
} from '@ion/core/src/units/attributes';
import {
  convertAVLEvent,
  getLocalizedAttribute
} from '@ion/core/src/units/helpers';
import {isFalse, isTrue} from '@ion/core/src/utils/BooleanUtils';
import {secondsToHours} from '@ion/core/src/utils/DateUtil';
import {toFixed} from '@ion/core/src/utils/NumberUtil';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {DiagnosticsAccordion as CurrentDiagnosticsAccordion} from '@ion/dls/src/components/assetdetails/current/DiagnosticsAccordion';
import {FaultCodesModal} from '@ion/dls/src/components/assetdetails/current/FaultCodesModal';
import {DeviceInputContent} from '@ion/dls/src/components/device/DeviceInputContent';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import {StatusIcon} from '@ion/dls/src/icons/StatusIcon';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme, DtcEventType, IconStatus} from '@ion/dls/src/types/types';
import {resolveValue} from '@ion/dls/src/utils/ComponentUtils';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch} from '@ion/query/src/utils/helpers';
import {getDeviceEntityId} from '@/store/helpers';
import {AssetDetailsSection, useRegisteredAccordion} from './assetDetailsAccordionStore';
import {FaultChipData} from './FaultChipData';
import {getBatteryLevel, useDiagnosticTileData, useInputsRowData} from './useDiagnosticTileData';
import {useFaultCodesModal} from './useFaultCodesModal';

export type AssetDetailsDiagnosticsProps = {
  asset: Device;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    content : {
      padding : '0 15px'
    },
    loader : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    time : {
      color         : dlsColor('grey4'),
      marginLeft    : '10px',
      textTransform : 'uppercase',
      fontSize      : '10px',
      fontFamily    : 'Roboto',
      fontWeight    : 500,
      letterSpacing : '0.5px',
      display       : 'flex',
      alignItems    : 'center',
      height        : '40px'
    },
    alignment : {
      display    : 'inline-flex',
      alignItems : 'center'
    },
    enginStatusContainer : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    altStatusItemTypography : {
      ...dlsTypography('chipButton'),
      textAlign : 'right',
      color     : dlsColor('caRed'),
      padding   : '0 !important'
    },
    diagnostics : {
      ...dlsTypography('panelCards'),
      fontFamily : 'Roboto',
      fontWeight : '600',
      fontSize   : '14px',
      lineHeight : '17px',
      color      : dlsColor('black'),
      minHeight  : 48
    },
    assetDetailsDiagnostics : {
      padding : '0 15px'
    },
    ...getAccordionWrapperStyles()
  };
});

/**
 * @AssetDetailsDiagnostics
 */
export const AssetDetailsDiagnostics = ({asset}: AssetDetailsDiagnosticsProps) => {
  const {
    engineStatus,
    odometer,
    engineHours,
    fuelLevel,
    deviceBattery,
    batteryLevel,
    faults
  } = translate('tracking:assetDetail.diagnostics', {returnObjects: true});
  const [isLoading, setIsLoading]                 = useState(true);
  const [faultData, setFaultData]                 = useState([]);
  const [dtcEventType, setdtcEventType]           = useState(null);
  const classes                                   = useStyles({dtcEventType});
  const [eventTime, setEventTime]                 = useState(null);
  const [deviceDiagnostics, setDeviceDiagnostics] = useState<any>({});
  const diagnosticsFinalDataRef                   = useRef<any>({});
  const isCurrent                                 = isCurrentTheme();
  // called unconditionally (Rules of Hooks) - only rendered when the
  // current theme branch below is actually taken ...
  const tiles                                     = useDiagnosticTileData(asset, deviceDiagnostics);
  const inputs                                    = useInputsRowData(asset?.getDevice());
  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // current theme only, the legacy Accordion below stays uncontrolled ...
  const {
    expanded : isDiagnosticsExpanded,
    onExpand : onDiagnosticsExpand
  } = useRegisteredAccordion(AssetDetailsSection.diagnostics, false, isCurrent);
  // the current theme's Fault Codes modal - its open state, columns/rows
  // and Export handler; the legacy accordion below uses `FaultChipData`
  // directly from `faultData` and never opens this modal ...
  const {faultCodes, modal: faultCodesModal} = useFaultCodesModal({asset, faultData, dtcEventType, isCurrent});

  const useComponentWillMount = (func: { (): Promise<void>; (): unknown; }) => {
    useMemo(func, []);
  };

  const getEngineStatus = (status: any) => {
    if (isTrue(status)) {
      return (
        <div className={classes.enginStatusContainer}>
          <StatusIcon type={IconStatus.off} />
          <Typography className={classes.altStatusItemTypography}>
            {translate('tracking:assetDetail.diagnostics.engineON')}
          </Typography>
        </div>
      );
    } else if (isFalse(status)) {
      return translate('tracking:assetDetail.diagnostics.engineOFF');
    }
    return '';
  };

  const getTime = (eventTime: any) => {
    const str = eventTime;
    const res = str?.replace('hours', 'hrs');
    return res;
  };

  useEffect(() => {
    generateFaultData(asset?.getDevice());
  }, [asset]);

  const convertAssetToDiagnostics = () => {
    const device = asset?.getDevice();
    return {
      EVBatteryPackRemainingCharge : device?.evBatteryPackRemainingCharge,
      ADCDeviceBatteryVoltage      : device?.adcDeviceBatteryVoltage,
      ADCExternalPowerVoltage      : device?.adcExternalPowerVoltage,
      odometer                     : getOdometerVal(device, deviceDiagnostics),
      vbusMilStatus                : device?.vbusMilStatus,
      VBFuelPercent                : device?.vbFuelPercent,
      systemOfUnits                : device?.systemOfUnits,
      engineHour                   : secondsToHours(device?.engineHour),
      eventTime                    : device?.eventTime
    };
  };

  async function getFaultData(device: { esn: any; }) {
    const deviceId = getDeviceEntityId(device);
    // without a valid id there is nothing to ask for ...
    if (!deviceId) {
      return null;
    }
    const augumentDevice = {
      deviceEsn : device.esn || 0,
      deviceId
    } as unknown;
    let dtcEvents = await cacheOnFetch({
      queryKey : createKey(QueryKey.GET_DTC_CODES, deviceId, false),
      queryFn  : async() => await getDTCCodes({device: augumentDevice})
    }) as any;
    if (!dtcEvents || !Object.keys(dtcEvents)?.length) {
      dtcEvents = await cacheOnFetch({
        queryKey : createKey(QueryKey.GET_DTC_CODES, deviceId, true),
        queryFn  : async() => await getDTCCodes({device: augumentDevice, useDTC2Report: true})
      }) as any;
    }
    return dtcEvents;
  }

  async function getLmuDeviceDiagnostics(devices: any[]) {
    const promises = devices.map(async(device: any) => {
      // only a device with a valid id is fetched - never `/devices/undefined` ...
      const deviceId     = getDeviceEntityId(device);
      const deviceEntity = deviceId ? await cacheOnFetch({
        queryKey : createKey(QueryKey.DEVICE_GET, deviceId, false),
        queryFn  : async() => await getDevice(deviceId)
      }) as any : null;
      if (deviceEntity && deviceEntity[0]?.category !== 'CAMERA') {
        const resp             = await getEvents(deviceId);
        const resultsEvents    = resp.results || [];
        const latestEvent      = resultsEvents.find((events) => events.avlEvent);
        const diagnosticValues = {} as any;
        if (latestEvent) {
          latestEvent?.avlEvent?.deviceDataConverted?.accumulators?.forEach(({label, value}) => {
            diagnosticValues[label.replace(/\s/g, '')] = value;
          });
          const electedValues = latestEvent?.avlEvent?.deviceDataConverted?.electedValues || {};
          Object.keys(electedValues).forEach(key => {
            if (key === 'engineHour' && electedValues[key].label === 'EngineHours') {
              diagnosticValues.engineHour = secondsToHours(electedValues[key].value);
            } else {
              diagnosticValues[key] = electedValues[key].value;
            }
          });
  
          diagnosticValues.vbusMilStatus = latestEvent?.avlEvent?.vbusIndicators?.milStatus;
          diagnosticValues.eventTime = latestEvent?.avlEvent?.eventTime;
          diagnosticValues.systemOfUnits = latestEvent?.avlEvent?.deviceDataConverted?.systemOfUnits;
        }
        return diagnosticValues;
      }
    });

    const diagnosticsArray = await Promise.all(promises);
    // starts from nothing, so a list with no device left to read reduces to
    // the asset's own values below rather than throwing ...
    diagnosticsFinalDataRef.current = diagnosticsArray.filter(Boolean)
      .reduce((obj1, obj2) => (obj1?.eventTime > obj2?.eventTime ? obj1 : obj2), {});
    if (isEmpty(diagnosticsFinalDataRef.current)) {
      diagnosticsFinalDataRef.current = convertAssetToDiagnostics();
    }
    const device = asset?.getDevice();
    setDeviceDiagnostics({
      ...diagnosticsFinalDataRef.current,
      engineHour : secondsToHours(device?.engineHour),
      odometer   : getOdometerVal(device, diagnosticsFinalDataRef.current)
    });
    if (diagnosticsFinalDataRef.current?.eventTime) {
      setEventTime(moment(new Date(diagnosticsFinalDataRef.current?.eventTime)).utc().fromNow());
    }
  }

  async function generateFaultData(device: BaseAsset) {
    setIsLoading(true);
    setFaultData([]);
    setEventTime(null);
    setDeviceDiagnostics({});
    try {
      const faultData = [];
      const eventTimeList = [];
      const devices = device?.devices;
      await getLmuDeviceDiagnostics(devices);
      if (devices?.length) {
        const allEvents = await Promise.all(devices.map(async(device: any) => await getFaultData(device)));
        allEvents.forEach(result => {
          result?.forEach((dtcEvent: { j1939DtcBlocks: any[]; eventTime: any; }) => {
            const {codes, dtcCodes, eventTime} = dtcEvent.j1939DtcBlocks?.[0] || dtcEvent;
            if (codes?.length || dtcCodes?.length) {
              setdtcEventType(dtcCodes ? DtcEventType.DtcEvents : DtcEventType.Dtc2Events);
              faultData.push(...(dtcCodes || codes || []));
            }
            eventTimeList.push(new Date(eventTime || dtcEvent.eventTime));
          });
        });
      }
      const filteredFaultCodes = removeDuplicate('dtcCode', faultData);
      if (!diagnosticsFinalDataRef.current?.eventTime && eventTimeList.length) {
        setEventTime(moment(new Date(Math.max(...eventTimeList))).utc().fromNow());
      }
      setFaultData(filteredFaultCodes);
    } catch (e) {
      error(e);
    }
    setIsLoading(false);
  }

  const getOdometerVal = (asset: CVSAssetType, deviceDiagnosticsData: any): string => {
    const finalAssetData = asset?.odometer ? asset : deviceDiagnosticsData;
    const {odometer} = convertAVLEvent({...finalAssetData}) || {};
    const latestEventOdometer = odometer;
    // guards against a non-numeric-parseable `odometer` (e.g. a raw string
    // that doesn't convert cleanly) reaching `toFixed`, which would return
    // `NaN` and get silently baked into the returned string as "NaN mi" ...
    if (latestEventOdometer && finalAssetData?.systemOfUnits && !isNaN(Number(latestEventOdometer))) {
      const units = getLocalizedAttribute(Attribute.ODOMETER);
      return `${toFixed(latestEventOdometer, 1)} ${units}`;
    }
    return resolveValue(latestEventOdometer);
  };

  return isCurrent ? (
    <>
      <CurrentDiagnosticsAccordion lastReportedAt={eventTime || undefined}
        tiles={tiles}
        faultCodes={faultCodes}
        inputs={inputs}
        expanded={isDiagnosticsExpanded}
        onExpand={onDiagnosticsExpand} />
      <FaultCodesModal {...faultCodesModal} />
    </>
  ) : (
    <div className={classes.accordionWrapper}>
      <Accordion className={classes.assetDetailsDiagnostics}
        header={
          <div className={classes.alignment}>
            <div className={classes.diagnostics}>
              {translate('tracking:assetDetail.diagnostics.header')}
            </div>
            <span className={classes.time}>{getTime(eventTime)}</span>
          </div>
        }
        defaultExpanded={isTrue(deviceDiagnostics?.vbusMilStatus)}
        details={
          <div className={classes.content}>
            <Render.If condition={deviceDiagnostics?.vbusMilStatus}>
              <CardItemRow label={engineStatus}
                value={resolveValue(getEngineStatus(deviceDiagnostics?.vbusMilStatus))}
                isBottomMargin={false}
                isCondensedVariant />
            </Render.If>
            <Render.If condition={deviceDiagnostics?.odometer}>
              <CardItemRow label={odometer}
                value={deviceDiagnostics?.odometer}
                isBottomMargin={false}
                isCondensedVariant />
            </Render.If>
            <Render.If condition={deviceDiagnostics?.engineHour}>
              <CardItemRow label={engineHours}
                value={(deviceDiagnostics?.engineHour)?.toString()}
                isBottomMargin={false}
                isCondensedVariant />
            </Render.If>
            <Render.If condition={deviceDiagnostics?.VBFuelPercent &&
              isNil(deviceDiagnostics?.EVBatteryPackRemainingCharge)}>
              <CardItemRow label={fuelLevel}
                value={`${deviceDiagnostics?.VBFuelPercent} %`}
                isBottomMargin={false}
                isCondensedVariant />
            </Render.If>
            <Render.If condition={deviceDiagnostics?.ADCDeviceBatteryVoltage &&
                deviceDiagnostics?.ADCExternalPowerVoltage}>
              <CardItemRow label={batteryLevel}
                value={`${resolveValue(getBatteryLevel(
                  deviceDiagnostics?.ADCDeviceBatteryVoltage, deviceDiagnostics?.ADCExternalPowerVoltage))} V`}
                isBottomMargin
                isCondensedVariant
                isTextRightAligned />
            </Render.If>
            <DeviceInputContent asset={asset?.getDevice()} />
            {isLoading ? <div className={classes.loader}><IonSpinner /></div> : (
              (faultData && faultData.length > 0) && (
                <CardItemRow label={faults}
                  value={(
                    <FaultChipData events={faultData}
                      eventType={dtcEventType} />
                  )}
                  isBottomMargin={false}
                  isCondensedVariant />
              )
            )}
          </div>
        }
        borderBottom={'none'}
        padding={'15px 0'}
        isCondensedVariant />
    </div>
  );
};
export default AssetDetailsDiagnostics;
